import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { AssetUploadEditorFormType } from '../../shared/asset-upload-editor/asset-upload-editor-form-type';
import { ToastService } from 'src/app/base/services/toast.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AssetImageEditorComponent } from '../../shared/asset-image-editor/asset-image-editor.component';
import { AssetUploadEditorComponent } from '../../shared/asset-upload-editor/asset-upload-editor.component';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { SelectingEventArgs, TabComponent, TabModule } from '@syncfusion/ej2-angular-navigations';
import { BrandLogosService } from '../brand-logos.service';
import { AssetType, AssetBrandDTO, BrandLogoDTO, CreateBrandLogoDTO } from 'src/app/base/models/models.swagger.generated';
import { ErrorType } from 'src/app/core/routing/error-types';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-brand-logos-edit-form',
    templateUrl: './brand-logos-edit-form.component.html',
    styleUrls: ['./brand-logos-edit-form.component.scss'],
    imports: [DialogModule, TabModule, AssetUploadEditorComponent, AssetImageEditorComponent, TranslateModule]
})
export class BrandLogosEditFormComponent implements OnInit, AfterViewChecked {

  @ViewChild('tabObj')
  public tabObj: TabComponent;

  @ViewChild("editProductAssetDialog")
  public dialog: DialogComponent;

  @ViewChild("assetUploadEditorComponent")
  public assetUploadEditorComponent: AssetUploadEditorComponent;

  @ViewChild("assetImageEditorComponent")
  public assetImageEditorComponent: AssetImageEditorComponent;

  @Output()
  public OnClose: EventEmitter<any> = new EventEmitter<any>();

  public animation: object = {
    previous: { effect: "", duration: 0, easing: "" },
    next: { effect: "", duration: 0, easing: "" }
  };

  public assetUploadEditorForm: FormGroup<AssetUploadEditorFormType>;

  public isFullScreen: boolean = false;
  public isLoading = false;
  public showImageEditor: boolean = false;

  private selectedBrands: AssetBrandDTO[] = [];

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private brandLogosService: BrandLogosService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit(): void {
    this.assetUploadEditorForm = this.fb.group<AssetUploadEditorFormType>({
      id: this.fb.control<number>(null),
      publicName: this.fb.control<string>(null, [Validators.required, Validators.pattern(/^[a-z0-9_-]+$/)]),
      originalFileName: this.fb.control<string>({ value: null, disabled: true }),
      fileExtension: this.fb.control<string>({ value: null, disabled: true }),
      fileLink: this.fb.control<string>(null),
      language: this.fb.control<string>(null, [Validators.required]),
      type: this.fb.control<AssetType>({ value: AssetType.Logo, disabled: true }),
      isKey: this.fb.control<boolean>(false, [Validators.required]),
      shopIds: this.fb.control<string[]>(['LITTLEBIT'], [Validators.required]),
      base64FileData: this.fb.control<string>(null, [Validators.required]),
      totalSize: this.fb.control<number>(null),
      localFilePath: this.fb.control<string>({ value: null, disabled: true }),
      replaceProductLogos: this.fb.control<boolean>(false),
      replaceCustomizedLogos: this.fb.control<boolean>({ value: false, disabled: true }),
      altText: this.fb.control<string>(null),
      intermediateFileInfo: this.fb.control<any>(null)
    });
  }

  public ngAfterViewChecked(): void {
    // get regular expression to allow only lowercase letters, numbers, and minus sign
    const regex = /^[a-z0-9_]+$/;


    this.cdr.detectChanges();
  }

  public tabGroupCreated() {
    this.setTabVisibility();
    this.assetUploadEditorForm.controls.fileExtension.valueChanges.subscribe((value) => {
      this.setTabVisibility();
      this.assetImageEditorComponent?.imageEditorObj?.update();
    });
  }

  public assignLogos(selectedBrandSubbrands: BrandLogoDTO[]) {
    this.selectedBrands = selectedBrandSubbrands.map((p) => {
      return new AssetBrandDTO({ brand: p.brand, brandSub: p.brandSub });
    });
    this.setValidators();
    this.dialog.show(false);
  }

  public onCancel() {
    if (this.isLoading) {
      return;
    }
    this.dialog.hide();
    this.resetDialog();
  }

  public onOpen(args: any): void {
    //Preventing the default dialog focus
    args.preventFocus = true;
  }

  public onSave() {
    if (this.assetImageEditorComponent?.hasPendingChanges()) {
      this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.title',
        'i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.message', null);
      return;
    }
    if (!this.assetUploadEditorForm.valid) {
      this.assetUploadEditorForm.markAllAsTouched();
      this.assetUploadEditorForm.markAsDirty();
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.form-invalid.title',
          'i18n.domain.assets.product-assets.edit-asset.form-invalid.message', null);
      }
      return;
    }

    if (this.assetUploadEditorForm.controls.replaceCustomizedLogos.value || this.assetUploadEditorForm.controls.replaceProductLogos.value) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.assets.product-assets.edit-asset.messages.save-brand-logo-dialog.title', 'i18n.domain.assets.product-assets.edit-asset.messages.save-brand-logo-dialog.question');
      dialog.onOkSubject.subscribe(() => {
        this.saveAsset();
      });
    } else {
      this.saveAsset();
    }
  }

  public select(event: SelectingEventArgs): void {
    if (this.dialog.visible) {
      if (event.selectingIndex === 0) {
        if (this.assetImageEditorComponent?.hasPendingChanges()) {
          this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.title',
            'i18n.domain.assets.product-assets.edit-asset.unsaved-image-data.message', null);
          event.cancel = true;
          return;
        }
        if (this.assetImageEditorComponent?.hasBeenEdited()) {
          this.getImageDataFromCanvas();
        }
        this.showImageEditor = false;
      }
      if (event.selectingIndex === 1) {
        this.showImageEditor = true;
      }
    }

    if (this.isFullScreen) {
      this.dialog.show(false);
      this.isFullScreen = false;
    }

    if (event.isSwiped) {
      event.cancel = true;
      return;
    }
  }

  public toggleFullScreen() {
    this.isFullScreen ? this.dialog.show(false) : this.dialog.show(true);
    this.isFullScreen = !this.isFullScreen;
  }

  private setTabVisibility() {
    var extension = this.assetUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    this.tabObj?.hideTab(1, extension !== "jpg" &&
      extension !== "jpeg" &&
      extension !== "png");
  }

  private resetDialog() {
    this.isLoading = false;
    this.assetUploadEditorForm.setValue({
      id: null,
      publicName: null,
      originalFileName: null,
      fileExtension: null,
      fileLink: null,
      language: null,
      type: AssetType.Logo,
      isKey: false,
      shopIds: ['LITTLEBIT'],
      base64FileData: null,
      totalSize: null,
      localFilePath: null,
      replaceProductLogos: false,
      replaceCustomizedLogos: false,
      altText: null,
      intermediateFileInfo: null
    });
    this.assetUploadEditorForm.controls.type.disable();
    this.isFullScreen = false;
    this.assetUploadEditorForm.markAsUntouched();
    this.assetUploadEditorForm.markAsPristine();
    this.assetUploadEditorForm.updateValueAndValidity();
    this.tabObj?.select(0);
    this.assetUploadEditorComponent.clearUploader();
  }

  private saveAsset() {
    if (this.isLoading) {
      return;
    }
    var extension = this.assetUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    if ((extension == "jpg" ||
      extension == "jpeg" ||
      extension == "png") &&
      this.assetImageEditorComponent?.imageEditorObj) {
      this.getImageDataFromCanvas();
    }

    this.isLoading = true;
    this.brandLogosService.createBrandLogo(this.fillCreateBrandLogoDTO()).subscribe({
      next: () => {
        this.isLoading = false;
        this.OnClose.emit();
        this.dialog.hide();
        this.resetDialog();
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private fillCreateBrandLogoDTO(): CreateBrandLogoDTO {
    return {
      brands: this.selectedBrands,
      publicName: this.assetUploadEditorForm.controls.publicName.value,
      originalFileName: this.assetUploadEditorForm.controls.localFilePath.value,
      fileExtension: this.assetUploadEditorForm.controls.fileExtension.value,
      shops: this.assetUploadEditorForm.controls.shopIds.value,
      base64FileData: this.assetUploadEditorForm.controls.base64FileData.value,
      isKey: this.assetUploadEditorForm.controls.isKey.value,
      language: this.assetUploadEditorForm.controls.language.value,
      replaceAllProductLogos: this.assetUploadEditorForm.controls.replaceProductLogos.value,
      replaceCustomLogos: this.assetUploadEditorForm.controls.replaceCustomizedLogos.value,
      altText: this.assetUploadEditorForm.controls.altText.value
    } as unknown as CreateBrandLogoDTO;
  }

  private getImageDataFromCanvas() {
    //Save edited image
    var imageData = this.assetImageEditorComponent.imageEditorObj.getImageData();
    var canvas = document.createElement('CANVAS') as HTMLCanvasElement;
    canvas.height = imageData.height;
    canvas.width = imageData.width;
    var ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);
    var dataURL = canvas.toDataURL(MimeTypeHelper.getMimeType(this.assetUploadEditorForm.controls.fileExtension.value));
    this.assetUploadEditorForm.controls.base64FileData.setValue(dataURL);
  }

  private setValidators() {
    this.assetUploadEditorForm.controls.language.disable();
    this.assetUploadEditorForm.controls.language.setValue(null);
    this.assetUploadEditorForm.controls.language.setValidators(null);
    this.assetUploadEditorForm.controls.language.updateValueAndValidity();
  }
}
